// Цифры сотрудника отдела продаж: одни и те же блоки в личном кабинете и в
// карточке менеджера у руководителя и главных.

import { Link } from 'react-router-dom';
import clsx from 'clsx';

import type { BackState } from '@/lib/back-nav';
import { formatBps, formatDate, formatKop } from '@/lib/format';
import { monthLong } from '@/lib/period';
import { pluralEstablishments } from '@/lib/subscription';

export interface MemberStatsData {
  windowDays: number;
  establishments: number;
  payingEstablishments: number;
  newEstablishments30d: number;
  earnedKopecks: number;
  pendingKopecks: number;
  paidOutKopecks: number;
  payoutsCount: number;
  commission30dKopecks: number;
  subscriptions30dKopecks: number;
  revenue30dKopecks: number;
}

export interface AccrualItem {
  id: string;
  establishmentId: string | null;
  establishmentName: string;
  kind: 'COMMISSION' | 'STORNO';
  paymentKopecks: number;
  bps: number;
  amountKopecks: number;
  paidAt: string;
  paidOut: boolean;
}

export interface MonthItem {
  month: string;
  amountKopecks: number;
  accruals: number;
}

export interface PayoutItem {
  id: string;
  amountKopecks: number;
  method: string;
  periodFrom: string;
  periodTo: string;
  comment: string | null;
  createdAt: string;
  accrualsCount: number;
}

function Tile({ label, value, hint, muted }: { label: string; value: string; hint?: string; muted?: boolean }) {
  return (
    <div className="card p-4">
      <div className="text-xs text-ink/50">{label}</div>
      <div className={clsx('mt-1 text-xl font-semibold tabular-nums', muted && 'text-ink/35')}>{value}</div>
      {hint ? <div className="mt-0.5 text-[11px] text-ink/45">{hint}</div> : null}
    </div>
  );
}

/** Деньги сотрудника сверху, его заведения снизу. */
export function MemberStatsGrid({ stats: s }: { stats: MemberStatsData }) {
  const days = `${s.windowDays} дн.`;
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
      <Tile label="К выплате" value={formatKop(s.pendingKopecks)} />
      <Tile
        label="Заработано всего"
        value={formatKop(s.earnedKopecks)}
        hint={s.commission30dKopecks ? `за ${days} ${formatKop(s.commission30dKopecks)}` : `за ${days} начислений нет`}
      />
      <Tile
        label="Выплачено"
        value={formatKop(s.paidOutKopecks)}
        hint={s.payoutsCount ? `выплат: ${s.payoutsCount}` : 'выплат ещё не было'}
        muted={!s.paidOutKopecks}
      />
      <Tile
        label="Заведений"
        value={String(s.establishments)}
        hint={`платят ${s.payingEstablishments} · новых за ${days} ${s.newEstablishments30d}`}
      />
      <Tile
        label={`Оплаты подписки, ${days}`}
        value={formatKop(s.subscriptions30dKopecks)}
        hint="с его заведений, за вычетом возвратов"
        muted={!s.subscriptions30dKopecks}
      />
      <Tile
        label={`Оборот заведений, ${days}`}
        value={formatKop(s.revenue30dKopecks)}
        hint="выручка через кассу"
        muted={!s.revenue30dKopecks}
      />
    </div>
  );
}

/** Лента «кто оплатил»: начисления по платежам подписки. */
export function AccrualsList({
  items,
  emptyText,
  backState,
}: {
  items: AccrualItem[];
  emptyText: string;
  backState?: BackState;
}) {
  return (
    <section className="card overflow-hidden">
      <div className="border-b border-line px-4 py-3 text-sm font-medium">Последние оплаты</div>
      {items.length === 0 ? (
        <div className="px-4 py-6 text-sm text-ink/50">{emptyText}</div>
      ) : (
        <ul className="divide-y divide-line/60">
          {items.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                {p.establishmentId ? (
                  <Link
                    to={`/establishments/${p.establishmentId}`}
                    state={backState}
                    className="block truncate font-medium hover:text-orange"
                  >
                    {p.establishmentName}
                  </Link>
                ) : (
                  <div className="truncate font-medium">{p.establishmentName}</div>
                )}
                <div className="mt-0.5 text-xs text-ink/50">
                  {formatDate(p.paidAt)} · подписка {formatKop(p.paymentKopecks)} · {formatBps(p.bps)}
                  {p.kind === 'STORNO' ? ' · возврат' : ''}
                </div>
              </div>
              <div className="shrink-0 text-right">
                <div className={clsx('font-semibold tabular-nums', p.kind === 'STORNO' ? 'text-rose-600' : 'text-emerald-600')}>
                  {p.kind === 'STORNO' ? '' : '+'}
                  {formatKop(p.amountKopecks)}
                </div>
                <div className="text-[11px] text-ink/40">{p.paidOut ? 'выплачено' : 'к выплате'}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function MonthsTable({ rows }: { rows: MonthItem[] }) {
  if (!rows.length) return null;
  return (
    <section className="card overflow-hidden">
      <div className="border-b border-line px-4 py-3 text-sm font-medium">Начисления по месяцам</div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-ink/50">
            <th className="px-4 py-2 font-medium">Месяц</th>
            <th className="px-4 py-2 font-medium">Начислений</th>
            <th className="px-4 py-2 text-right font-medium">Сумма</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.month} className="border-b border-line/60 last:border-0">
              <td className="px-4 py-2">{monthLong(row.month)}</td>
              <td className="px-4 py-2 tabular-nums">{row.accruals}</td>
              <td className="px-4 py-2 text-right font-medium tabular-nums">{formatKop(row.amountKopecks)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

const PAYOUT_METHOD: Record<string, string> = { cash: 'наличные', transfer: 'перевод' };

export function PayoutsList({ items }: { items: PayoutItem[] }) {
  return (
    <section className="card overflow-hidden">
      <div className="border-b border-line px-4 py-3 text-sm font-medium">Выплаты</div>
      {items.length === 0 ? (
        <div className="px-4 py-6 text-sm text-ink/50">Выплат пока не было — их проводит администратор Resstro.</div>
      ) : (
        <ul className="divide-y divide-line/60">
          {items.map((p) => (
            <li key={p.id} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <div className="font-semibold tabular-nums">{formatKop(p.amountKopecks)}</div>
                <div className="mt-0.5 text-xs text-ink/50">
                  {PAYOUT_METHOD[p.method] ?? 'другое'} · начислений {p.accrualsCount} · за{' '}
                  {formatDate(p.periodFrom)} — {formatDate(p.periodTo)}
                </div>
                {p.comment ? <div className="mt-0.5 text-xs text-ink/45">{p.comment}</div> : null}
              </div>
              <div className="shrink-0 text-xs text-ink/45">{formatDate(p.createdAt)}</div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** «3 заведения» — для подписей в карточках. */
export function establishmentsCount(n: number): string {
  return `${n} ${pluralEstablishments(n)}`;
}
